import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Shield, Zap, Users, Star, PhoneCall, Wifi, Smartphone, TrendingDown, Clock, Award, Headphones, ArrowLeftRight, Calendar } from 'lucide-react';
import Hero from '../components/Hero';
import AffiliatePrograms from '../components/AffiliatePrograms';
import Testimonials from '../components/Testimonials';
import ProviderLogos from '../components/ProviderLogos';
import { useLanguage } from '../i18n/LanguageContext';
import { getRecentBlogPosts, BLOG_POSTS } from '../data/blog';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.12 } }),
};

const HomePage: React.FC = () => {
  const { t, lang } = useLanguage();
  const recentPosts = getRecentBlogPosts(3);

  return (
    <div>
      <Helmet>
        <title>Easy Vertrag Völklingen - Handyvertrag, Smartphone & Mobilfunk | Saarland</title>
        <meta name="description" content="Easy Vertrag in Völklingen (Saarland) - Ihr lokaler Partner im Saarland für Handyverträge, Smartphones, Internet und Energie. Vodafone, O2 & Sparhandy kostenlos vergleichen. Jetzt Sparen" />
        <meta name="keywords" content="Handyvertrag Völklingen, Smartphone Saarland, Mobilfunk Völklingen, Handytarif Saarbrücken, Internet Völklingen, Easy Vertrag, Vodafone, O2" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/" />
        <meta property="og:title" content="Easy Vertrag Völklingen - Handyvertrag, Smartphone &amp; Mobilfunk | Saarland" />
        <meta property="og:description" content="Easy Vertrag in Völklingen (Saarland) – Ihr lokaler Partner für Handyverträge, Smartphones, Internet &amp; Strom. Vodafone, O2 &amp; 1&amp;1 vergleichen. Jetzt sparen!" />
        <meta property="og:url" content="https://easy4vertrag.de/" />
        <meta property="og:image" content="https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="de_DE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Easy Vertrag Völklingen - Handyvertrag, Smartphone & Mobilfunk | Saarland" />
        <meta name="twitter:description" content="Ihr lokaler Partner für Handyverträge, Smartphones, Internet & Strom im Saarland." />
        <meta name="twitter:image" content="https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Easy Vertrag",
            "url": "https://easy4vertrag.de",
            "logo": "https://easy4vertrag.de/vite.svg",
            "description": "Ihr vertrauenswürdiger Partner für Smartphones, Tablets, Smartwatches und Handytarife in Deutschland.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Bismarck Straße 31",
              "addressLocality": "Völklingen",
              "postalCode": "66333",
              "addressCountry": "DE"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+49-179-7473491",
              "contactType": "customer service",
              "availableLanguage": "German",
              "hoursAvailable": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
                "opens": "10:00",
                "closes": "20:00"
              }
            },
            "sameAs": []
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Easy Vertrag",
            "description": "Ihr lokaler Partner für Handyverträge, Smartphones, Internet und Energie in Völklingen und dem Saarland.",
            "image": "https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg",
            "url": "https://easy4vertrag.de",
            "telephone": "+49-179-7473491",
            "email": "easy90az@gmail.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Bismarck Straße 31",
              "addressLocality": "Völklingen",
              "postalCode": "66333",
              "addressRegion": "Saarland",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 49.2534,
              "longitude": 6.8588
            },
            "areaServed": [
              {"@type": "City", "name": "Völklingen"},
              {"@type": "City", "name": "Saarbrücken"},
              {"@type": "City", "name": "Wadgassen"},
              {"@type": "City", "name": "Dillingen"},
              {"@type": "City", "name": "Saarlouis"},
              {"@type": "State", "name": "Saarland"}
            ],
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
                "opens": "10:00",
                "closes": "20:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Saturday"],
                "opens": "10:00",
                "closes": "18:00"
              }
            ],
            "priceRange": "€€",
            "hasMap": "https://www.google.com/maps?q=Bismarck+Stra%C3%9Fe+31,+66333+V%C3%B6lklingen",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "500",
              "bestRating": "5",
              "worstRating": "1"
            }
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Was ist Easy Vertrag?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Easy Vertrag ist ein unabhängiges Vergleichsportal für Mobilfunkverträge, Smartphones, Internet und Energietarife in Deutschland. Wir sind offizieller Affiliate-Partner der führenden deutschen Netzbetreiber und helfen Ihnen, das beste Angebot zu finden."
                }
              },
              {
                "@type": "Question",
                "name": "Wie kann ich bei Easy Vertrag sparen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Durch unsere exklusiven Partnerdeals mit Vodafone, O2 und Co. erhalten Sie Sonderkonditionen, die oft günstiger sind als direkte Angebote beim Anbieter. Zusätzlich vergleichen wir für Sie alle Angebote transparent, damit Sie nie zu viel zahlen."
                }
              },
              {
                "@type": "Question",
                "name": "Ist die Nutzung von Easy Vertrag kostenlos?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, unsere Vergleiche und Beratung sind für Sie vollständig kostenlos. Wir finanzieren uns durch Provisionen der Anbieter, wenn Sie über unsere Links einen Vertrag abschließen. Das hat keinen Einfluss auf den Preis für Sie."
                }
              },
              {
                "@type": "Question",
                "name": "Welche Anbieter werden verglichen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Wir vergleichen Angebote von Vodafone, O2, Blau, Freenet und vielen weiteren deutschen Anbietern. Unser Ziel ist ein vollständiger Marktüberblick für Sie."
                }
              },
              {
                "@type": "Question",
                "name": "Wie erreiche ich den Kundenservice?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sie erreichen uns per WhatsApp, E-Mail (easy90az@gmail.com) oder telefonisch unter +49 179 7473491. Montag bis Freitag von 10-20 Uhr, Samstag von 10-18 Uhr."
                }
              }
            ]
          }
        `}</script>
      </Helmet>
      <Hero />

{/* Affiliate Programs */}
      <AffiliatePrograms />

      {/* Trust Bar */}
      <section className="bg-white border-b border-neutral-100">
        <div className="container-custom py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <Shield className="w-5 h-5 text-primary-600" />, title: t('trustbar.kostenlos'), desc: t('trustbar.kostenlosDesc') },
              { icon: <Award className="w-5 h-5 text-accent-500" />, title: t('trustbar.exklusive'), desc: t('trustbar.exklusiveDesc') },
              { icon: <Headphones className="w-5 h-5 text-success-600" />, title: t('trustbar.beratung'), desc: t('trustbar.beratungDesc') },
              { icon: <Clock className="w-5 h-5 text-secondary-600" />, title: t('trustbar.schnell'), desc: t('trustbar.schnellDesc') },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 bg-neutral-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-neutral-800">{item.title}</p>
                  <p className="text-xs text-neutral-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Angebote - Empfohlene Angebote */}
      <section className="section-padding bg-neutral-50">
        <div className="container-custom">
          <motion.div
            className="text-center mb-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="badge-primary mb-3">Top Angebote</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-3">Empfohlene Angebote</h2>
            <p className="text-neutral-500 max-w-2xl mx-auto">
              Die besten Deals aus unseren Partnerprogrammen – exklusiv für unsere Kunden.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: 'DeinHandy',
                desc: 'Smartphones mit und ohne Vertrag zu Bestpreisen.',
                url: 'https://www.communicationads.net/tc.php?t=18676C13424564T',
                color: '#E60000',
                bg: '#FDEAEA',
                icon: <Smartphone className="w-6 h-6 text-white" />,
                img: '/Top Angebote/Bildschirmfoto_2026-08-03_um_01.13.29.png',
              },
              {
                title: 'MediaMarkt',
                desc: 'Elektronik & Technik vom Fachmarkt.',
                url: 'https://rast.mediamarkt.de/tc.php?t=18676C20940882B',
                color: '#CC0000',
                bg: '#FDEAEA',
                icon: <Smartphone className="w-6 h-6 text-white" />,
                img: '/Top Angebote/ChatGPT_Image_1._Aug._2026,_23_53_39.png',
              },
              {
                title: 'Otelo',
                desc: 'Günstige Handytarife ohne Aufpreis.',
                url: 'https://www.communicationads.net/tc.php?t=18676C14123184T',
                color: '#006B99',
                bg: '#E6F4F9',
                icon: <PhoneCall className="w-6 h-6 text-white" />,
                img: '/Top Angebote/Bildschirmfoto_2026-08-03_um_01.13.50.png',
              },
              {
                title: 'Samsung',
                desc: 'Galaxy Smartphones und Wearables.',
                url: 'https://www.communicationads.net/tc.php?t=18676C42647630B',
                color: '#1a4ba8',
                bg: '#E8EEF7',
                icon: <Smartphone className="w-6 h-6 text-white" />,
                img: '/Top Angebote/ChatGPT_Image_2._Aug._2026,_00_02_08.png',
              },
              {
                title: 'Sparhandy',
                desc: 'Vergleichsportal für Handytarife & DSL.',
                url: 'https://www.communicationads.net/tc.php?t=18676C19328056T',
                color: '#00A86B',
                bg: '#E6F7EE',
                icon: <Wifi className="w-6 h-6 text-white" />,
                img: '/Top Angebote/Bildschirmfoto_2026-08-03_um_01.14.01.png',
              },
              {
                title: 'Logitel',
                desc: 'Smartphones und Tarife im Vergleich.',
                url: 'https://www.communicationads.net/tc.php?t=18676C14328457B',
                color: '#7C3AED',
                bg: '#F3EEFD',
                icon: <Smartphone className="w-6 h-6 text-white" />,
                img: '/Top Angebote/Bildschirmfoto_2026-08-03_um_01.14.17.png',
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                className="group flex flex-col h-full rounded-2xl overflow-hidden shadow-soft hover:shadow-premium transition-all duration-300"
                style={{ border: `1px solid ${card.color}30` }}
              >
                <a
                  href={card.url}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="block relative overflow-hidden w-full"
                >
                  <div className="w-full h-44 overflow-hidden">
                    <img
                      src={card.img}
                      alt={card.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                </a>
                <div className="p-5 flex flex-col flex-1" style={{ backgroundColor: card.bg }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shadow-sm shrink-0"
                      style={{ backgroundColor: card.color }}
                    >
                      {card.icon}
                    </span>
                    <h3 className="font-bold text-neutral-900 text-base">{card.title}</h3>
                  </div>
                  <p className="text-sm text-neutral-600 leading-relaxed mb-4 flex-1">{card.desc}</p>
                  <a
                    href={card.url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="inline-flex items-center gap-1.5 text-sm font-bold transition-all hover:gap-2.5"
                    style={{ color: card.color }}
                  >
                    Zum Angebot <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Articles */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            className="text-center mb-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="badge-primary mb-3">Ratgeber & News</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-3">
              Aktuelle Artikel & Vergleiche
            </h2>
            <p className="text-neutral-500 max-w-2xl mx-auto">
              Informieren Sie sich über neue Smartphones, Handytarife und Netzvergleiche – profitieren Sie von unseren Experten-Tipps.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {BLOG_POSTS.slice(0, 6).map((post, i) => (
              <motion.article
                key={post.slug}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                className="group flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-premium transition-all duration-300"
              >
                <Link to={`/blog/${post.slug}`} className="flex flex-col h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-neutral-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readingTime} Min
                      </span>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-neutral-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-neutral-500 text-sm mb-4 line-clamp-2 flex-1">{post.excerpt}</p>
                    <span className="inline-flex items-center text-primary-600 font-medium text-sm group-hover:gap-2 transition-all">
                      Weiterlesen
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/blog" className="btn-primary">
              Alle Artikel ansehen <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Banner */}
      <section className="bg-gradient-to-r from-primary-700 to-primary-900 text-white py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm mb-5">
                <ArrowLeftRight className="w-4 h-4" />
                {t('home.comparisonBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">{t('home.comparisonTitle')}</h2>
              <p className="text-primary-100 mb-8 max-w-xl mx-auto">
                {t('home.comparisonDesc')}
              </p>
              <Link to="/produkte/handytarif" className="btn-accent text-base px-8 py-3.5">
                {t('home.tarifeVergleichen')} <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Service Area */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-10 items-center max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span className="badge-success mb-3">{t('home.localBadge')}</span>
              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-4">
                {t('home.localTitle')}
              </h2>
              <p className="text-neutral-600 mb-4">
                {t('home.localDesc1')}
              </p>
              <p className="text-neutral-600 mb-6">
                {t('home.localDesc2')}
              </p>
              <Link to="/kontakt" className="btn-primary">
                {t('home.localCta')} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              className="grid grid-cols-2 gap-3"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              {[
                { city: 'Völklingen', zip: '66333' },
                { city: 'Saarbrücken', zip: '66111-66133' },
                { city: 'Saarlouis', zip: '66740' },
                { city: 'Dillingen/Saar', zip: '66763' },
                { city: 'Wadgassen', zip: '66787' },
                { city: 'Quierschied', zip: '66287' },
              ].map((loc, i) => (
                <Link
                  key={i}
                  to="/kontakt"
                  className="bg-neutral-50 hover:bg-primary-50 rounded-xl p-4 text-center transition-colors group"
                >
                  <p className="font-semibold text-neutral-800 text-sm group-hover:text-primary-600 transition-colors">{loc.city}</p>
                  <p className="text-neutral-400 text-xs">{loc.zip}</p>
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Easy Vertrag - Trust Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: <Users className="w-7 h-7 text-primary-600" />, title: '500+ Kunden', desc: 'Zufriedene Kunden im Saarland', bg: 'bg-primary-50' },
                { icon: <Star className="w-7 h-7 text-accent-500" />, title: '4.8/5 Sterne', desc: 'Kundenzufriedenheit', bg: 'bg-accent-50' },
                { icon: <Shield className="w-7 h-7 text-success-600" />, title: 'Datenschutz', desc: 'DSGVO-konform', bg: 'bg-success-50' },
                { icon: <TrendingDown className="w-7 h-7 text-secondary-600" />, title: 'Bis 50% sparen', desc: 'Durch Vergleich', bg: 'bg-secondary-50' },
              ].map((card, i) => (
                <motion.div
                  key={i}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  whileHover={{ scale: 1.04, y: -4 }}
                  className={`${card.bg} p-5 rounded-2xl`}
                >
                  <div className="mb-2">{card.icon}</div>
                  <h3 className="font-bold text-neutral-800 text-sm">{card.title}</h3>
                  <p className="text-neutral-500 text-xs mt-1">{card.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span className="badge-primary mb-3">{t('home.whyBadge')}</span>
              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-4">
                {t('home.whyTitle')}
              </h2>
              <p className="text-neutral-600 mb-4">
                {t('home.whyDesc')}
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  t('home.why1'),
                  t('home.why2'),
                  t('home.why3'),
                  t('home.why4'),
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-neutral-700">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/uber-uns" className="btn-secondary">
                {t('home.whyCta')} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="badge-accent mb-3">{t('home.servicesBadge')}</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-3">{t('home.servicesTitle')}</h2>
            <p className="text-neutral-500 max-w-2xl mx-auto">
              {t('home.servicesDesc')}
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: 'Smartphones & Geräte',
                desc: 'iPhone, Samsung Galaxy, Tablets, Smartwatches und Kopfhörer – alle Top-Modelle mit oder ohne Vertrag zu den besten Preisen.',
                link: '/produkte/smartphones',
                cta: 'Smartphones vergleichen',
                color: 'border-primary-500',
                icon: <SmartphoneIcon />,
              },
              {
                title: 'Handytarife & Mobilfunk',
                desc: 'Alle Tarife von Vodafone, O2 im Vergleich. Mit Flatrate, 5G und flexibler Laufzeit.',
                link: '/produkte/handytarif',
                cta: 'Tarife vergleichen',
                color: 'border-success-500',
                icon: <PhoneCall className="w-6 h-6 text-success-600" />,
              },
              {
                title: 'Internet & Energie',
                desc: 'DSL, Glasfaser und Kabel-Internet sowie günstige Strom- und Gastarife – alles aus einer Hand.',
                link: '/internet-zuhause',
                cta: 'Internet vergleichen',
                color: 'border-accent-500',
                icon: <Zap className="w-6 h-6 text-accent-500" />,
              },
            ].map((service, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                whileHover={{ y: -6, boxShadow: '0 16px 32px rgba(0,0,0,0.1)' }}
                className={`bg-white rounded-2xl shadow-soft border-t-4 ${service.color} p-6`}
              >
                <div className="w-12 h-12 bg-neutral-50 rounded-xl flex items-center justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="font-bold text-neutral-900 text-lg mb-3">{service.title}</h3>
                <p className="text-neutral-500 text-sm mb-4">{service.desc}</p>
                <Link to={service.link} className="inline-flex items-center text-primary-600 font-medium text-sm hover:underline group">
                  {service.cta}
                  <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Landing Pages */}
      <section className="section-padding bg-neutral-50">
        <div className="container-custom">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="badge-success mb-3">{t('home.localBadge')}</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-3">{t('home.localPagesTitle')}</h2>
            <p className="text-neutral-500 max-w-xl mx-auto">
              {t('home.localPagesDesc')}
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                to: '/handyvertrag-voelklingen',
                gradient: 'from-primary-50 to-primary-100',
                border: 'border-primary-200 hover:border-primary-400',
                iconBg: 'bg-primary-600',
                icon: <PhoneCall className="w-6 h-6 text-white" />,
                title: 'Handyvertrag Völklingen',
                desc: 'Vodafone, O2 persönlich vergleichen. Ihr lokaler Ansprechpartner in Völklingen.',
              },
              {
                to: '/internet-anbieter-saarland',
                gradient: 'from-success-50 to-success-100',
                border: 'border-success-200 hover:border-success-400',
                iconBg: 'bg-success-600',
                icon: <Wifi className="w-6 h-6 text-white" />,
                title: 'Internet Saarland',
                desc: 'DSL, Glasfaser und Kabel-Internet im Saarland vergleichen. Die besten Anbieter für Ihre Adresse.',
              },
              {
                to: '/strom-gas-voelklingen',
                gradient: 'from-accent-50 to-accent-100',
                border: 'border-accent-200 hover:border-accent-400',
                iconBg: 'bg-accent-500',
                icon: <Zap className="w-6 h-6 text-white" />,
                title: 'Strom & Gas Völklingen',
                desc: 'Günstiger Strom- und Gasanbieter in Völklingen finden. Jetzt Tarife vergleichen und bis zu 400€ sparen.',
              },
            ].map((card, i) => (
              <motion.div key={card.to} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} whileHover={{ y: -6 }}>
                <Link
                  to={card.to}
                  className={`group block bg-gradient-to-br ${card.gradient} border ${card.border} rounded-2xl p-7 hover:shadow-lg transition-all duration-200 h-full`}
                >
                  <div className={`w-12 h-12 ${card.iconBg} rounded-xl flex items-center justify-center mb-5`}>
                    {card.icon}
                  </div>
                  <h3 className="font-bold text-neutral-900 text-lg mb-2">{card.title}</h3>
                  <p className="text-neutral-600 text-sm mb-4 leading-relaxed">{card.desc}</p>
                  <span className="inline-flex items-center text-primary-600 font-semibold text-sm group-hover:gap-2 transition-all">
                    {t('home.mehrErfahren')} <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span className="badge-primary mb-3">FAQ</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-3">Häufig gestellte Fragen</h2>
              <p className="text-neutral-500">Antworten auf die wichtigsten Fragen rund um Mobilfunk und Technik.</p>
            </motion.div>
            <div className="space-y-3">
              {[
                {
                  q: 'Was ist Easy Vertrag?',
                  a: 'Easy Vertrag ist ein unabhängiges Vergleichsportal für Mobilfunkverträge, Smartphones, Internet und Energietarife in Deutschland. Wir sind offizieller Affiliate-Partner der führenden deutschen Netzbetreiber und helfen Ihnen, das beste Angebot zu finden.',
                },
                {
                  q: 'Wie kann ich bei Easy Vertrag sparen?',
                  a: 'Durch unsere exklusiven Partnerdeals mit Vodafone, O2 und Co. erhalten Sie Sonderkonditionen, die oft günstiger sind als direkte Angebote beim Anbieter. Zusätzlich vergleichen wir für Sie alle Angebote transparent, damit Sie nie zu viel zahlen.',
                },
                {
                  q: 'Ist die Nutzung von Easy Vertrag kostenlos?',
                  a: 'Ja, unsere Vergleiche und Beratung sind für Sie vollständig kostenlos. Wir finanzieren uns durch Provisionen der Anbieter, wenn Sie über unsere Links einen Vertrag abschließen. Das hat keinen Einfluss auf den Preis für Sie.',
                },
                {
                  q: 'Welche Anbieter werden verglichen?',
                  a: 'Wir vergleichen Angebote von Vodafone, O2, Blau, Freenet und vielen weiteren deutschen Anbietern. Unser Ziel ist ein vollständiger Marktüberblick für Sie.',
                },
                {
                  q: 'Wie erreiche ich den Kundenservice?',
                  a: 'Sie erreichen uns per WhatsApp, E-Mail (easy90az@gmail.com) oder telefonisch unter +49 179 7473491. Montag bis Freitag von 10-20 Uhr, Samstag von 10-18 Uhr.',
                },
              ].map((item, i) => (
                <motion.details
                  key={i}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="bg-neutral-50 rounded-xl group"
                >
                  <summary className="font-semibold text-neutral-800 cursor-pointer list-none flex justify-between items-center p-5 hover:text-primary-600 transition-colors">
                    {item.q}
                    <ArrowRight className="w-4 h-4 text-neutral-400 flex-shrink-0 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="px-5 pb-5 text-neutral-600">{item.a}</p>
                </motion.details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Blog Teaser */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="badge-accent mb-3">{t('home.blogBadge')}</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-3">{t('home.blogTitle')}</h2>
            <p className="text-neutral-500 max-w-2xl mx-auto">
              {t('home.blogDesc')}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {recentPosts.map((post) => (
              <motion.div key={post.slug} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} whileHover={{ y: -6 }}>
                <Link to={`/blog/${post.slug}`} className="group block bg-neutral-50 rounded-2xl overflow-hidden hover:shadow-lg transition-all">
                  <div className="h-40 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-neutral-800 text-sm mb-2 group-hover:text-primary-600 transition-colors">{post.title}</h3>
                    <span className="inline-flex items-center text-primary-600 font-medium text-sm group">
                      {t('home.weiterlesen')} <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog" className="btn-secondary">
              {t('home.blogCta')} <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <ProviderLogos />
    </div>
  );
};

const SmartphoneIcon = () => (
  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5h3a1.5 1.5 0 011.5 1.5v18a1.5 1.5 0 01-1.5 1.5h-3A1.5 1.5 0 019 21V3a1.5 1.5 0 011.5-1.5z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 18h3" />
  </svg>
);

export default HomePage;
